import IdeyaModal from '../../../../../common/antd/ideya-modal.tsx'
import Tabs, { TabsProps } from 'antd/es/tabs'
import {
  UserBlockLeaveForm,
  UserBlockLeaveFormFieldTypes,
} from '../../user-block-leave-form/user-block-leave-form.tsx'
import { FormInstance } from 'antd/lib'
import { useGetBlockLeaveQuery } from '../../../../../apis/block-leave/block-leave-api.api.ts'
import { ColDef } from 'ag-grid-community'
import { IdeyaAgGridTable } from '../../../../../common/ag-grid/ideya-ag-grid-table.tsx'
import { marketDateTimezoneFormatter } from '../../../../../utils/formatters.ts'
import { IdeyaButton } from '../../../../../common/antd/ideya-button/ideya-button.tsx'
import { ICellRendererParams } from 'ag-grid-enterprise'
import { BlockLeaveData } from '../../../../../apis/block-leave/block-leave-api.def.ts'
import { Checkbox } from 'antd/lib/index'
import { CustomCellRendererProps } from 'ag-grid-react'
import { useTranslation } from 'react-i18next'
import './user-block-leave-combined-modal.scss'
import { useFixedBreakpoint } from '@/common/hooks/use-is-mobile.ts'
import { selectThemeStyle } from '@/features/setting/setting.slice.ts'
import { useAppSelector } from '@/redux/hooks.ts'

export type BlockLeaveTab = 'apply' | 'edit'

interface UserBlockLeaveCombinedModalProps {
  open: boolean
  userId: string | null
  activeTab: BlockLeaveTab
  onClose: () => void
  onTabChange: (tab: BlockLeaveTab) => void
  form: FormInstance<UserBlockLeaveFormFieldTypes>
  handleChangeLiquidate: (id: string, value: boolean) => void
  handleWithdraw: (id: string) => void
}

export const UserBlockLeaveCombinedModal: React.FC<
  UserBlockLeaveCombinedModalProps
> = ({
  open,
  userId,
  activeTab,
  onClose,
  onTabChange,
  form,
  handleChangeLiquidate,
  handleWithdraw,
}) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)

  const { data, isLoading, isFetching } = useGetBlockLeaveQuery(
    { userId: userId ?? '' },
    {
      skip: !userId,
    }
  )

  const screens = useFixedBreakpoint()

  const isBlockLeaveLoaded = !isLoading && !isFetching
  const hasBlockLeaveData = (data?.data?.length ?? 0) > 0
  const isEditDisabled = !isBlockLeaveLoaded || !hasBlockLeaveData

  const editColumns: ColDef[] = [
    {
      headerName: t(
        'user_management.user_modal.block_leave_modal.columns.created_at'
      ),
      field: 'createdAt',
      flex: screens.md ? 1 : 0,
      valueFormatter: (params) =>
        marketDateTimezoneFormatter(
          {
            ...params,
            data: {
              ...params.data,
              marketTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
          },
          { showTimezone: true }
        ),
      cellClass: (params) =>
        params.data?.deletedAt ? 'user-block-leave-modal__row--disabled' : '',
    },
    {
      headerName: t(
        'user_management.user_modal.block_leave_modal.columns.leave_dates'
      ),
      field: 'leaveDates',
      flex: screens.md ? 1 : 0,
      valueGetter: (params) => {
        const { startDate, endDate } = params.data
        return `${startDate} ~ ${endDate}`
      },
      cellClass: (params) =>
        params.data?.deletedAt ? 'user-block-leave-modal__row--disabled' : '',
    },
    {
      headerName: t(
        'user_management.user_modal.block_leave_modal.columns.liquidated_all_my_ideas'
      ),
      field: 'closeIdeas',
      flex: screens.md ? 1 : 0,
      cellClass: (params) =>
        params.data?.deletedAt ? 'user-block-leave-modal__row--disabled' : '',
      cellRenderer: (params: ICellRendererParams<BlockLeaveData>) => {
        return (
          <Checkbox
            checked={params.data?.closeIdeas || false}
            onChange={(e) =>
              handleChangeLiquidate(params.data!.id, e.target.checked)
            }
          />
        )
      },
    },
    {
      headerName: t(
        'user_management.user_modal.block_leave_modal.columns.action'
      ),
      field: '',
      suppressHeaderMenuButton: true,
      width: 125,
      cellClass: (params) =>
        params.data?.deletedAt ? 'user-block-leave-modal__row--disabled' : '',
      cellRenderer: (props: CustomCellRendererProps) => {
        const isDeleted = props.data?.deletedAt
        return (
          <IdeyaButton
            ideyaType={!isDeleted ? 'bw' : ''}
            disabled={isDeleted}
            className={'user-block-leave-modal__withdraw-btn'}
            onClick={() => {
              handleWithdraw(props.data.id)
            }}
            style={{
              width: '90%',
              ...(isDeleted
                ? {}
                : {
                    color: themeStyle.colorPrimaryText,
                    borderColor: themeStyle.colorPrimaryBorder,
                  }),
            }}
          >
            {isDeleted
              ? t(
                  'user_management.user_modal.block_leave_modal.columns.withdrawn'
                )
              : t(
                  'user_management.user_modal.block_leave_modal.columns.withdraw'
                )}
          </IdeyaButton>
        )
      },
    },
  ]

  const items: TabsProps['items'] = [
    {
      key: 'apply',
      label: t(
        'user_management.user_modal.block_leave_modal.apply_leave_dates'
      ),
      children: (
        <div className={'user-block-leave-modal__apply-content'}>
          <UserBlockLeaveForm
            handleCloseModal={onClose}
            userId={userId}
            form={form}
          />
        </div>
      ),
    },
    {
      key: 'edit',
      label: t('user_management.user_modal.block_leave_modal.edit_time_off'),
      disabled: isEditDisabled,
      children: (
        <div className={'user-block-leave-modal__edit-content'}>
          <IdeyaAgGridTable
            columns={editColumns}
            loading={isLoading || isFetching}
            rowData={data?.data || []}
            height={400}
          />
        </div>
      ),
    },
  ]

  return (
    <IdeyaModal
      open={open}
      title={t('user_management.user_modal.block_leave_modal.title')}
      destroyOnHidden
      isForm={true}
      width={800}
      onCancel={onClose}
      className={'user-block-leave-modal'}
    >
      <Tabs
        activeKey={activeTab}
        items={items}
        onChange={(key) => onTabChange(key as BlockLeaveTab)}
      />
    </IdeyaModal>
  )
}
